import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus, Trash2, Edit, ExternalLink, Globe, Languages, Loader2 } from "lucide-react";

const EMPTY = {
  slug: "", kind: "route", lang: "en",
  meta_title: "", meta_description: "", h1: "", subtitle: "", body_html: "",
  hero_image: "", og_image: "",
  origin: "", destination: "", city: "", district: "", vehicle_type: "",
  key_points: [], inclusions: [], faqs: [],
  price_from: "", price_note: "",
  related_slugs: [], primary_cta_label: "Get Instant Quote", primary_cta_url: "/custom-booking",
  whatsapp_cta_text: "", schema_service_type: "",
  active: true, priority: 0.6, change_freq: "monthly",
};

const KINDS = ["route", "vehicle", "location", "yatra", "service", "blog"];

export default function AdminSeoPages() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [langFilter, setLangFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pointsText, setPointsText] = useState("");
  const [inclText, setInclText] = useState("");
  const [relText, setRelText] = useState("");
  const [faqsText, setFaqsText] = useState("");

  const load = async () => {
    try { const r = await api.get("/seo-pages"); setItems(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ _new: true });
    setForm(EMPTY);
    setPointsText(""); setInclText(""); setRelText(""); setFaqsText("");
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...EMPTY, ...p });
    setPointsText((p.key_points || []).join("\n"));
    setInclText((p.inclusions || []).join("\n"));
    setRelText((p.related_slugs || []).join(", "));
    setFaqsText((p.faqs || []).map(f => `${f.q}\n${f.a}`).join("\n---\n"));
  };

  const save = async () => {
    const payload = {
      ...form,
      key_points: pointsText.split("\n").map(s => s.trim()).filter(Boolean),
      inclusions: inclText.split("\n").map(s => s.trim()).filter(Boolean),
      related_slugs: relText.split(",").map(s => s.trim()).filter(Boolean),
      faqs: faqsText.split("---").map(block => {
        const lines = block.trim().split("\n").filter(Boolean);
        return lines.length >= 2 ? { q: lines[0], a: lines.slice(1).join(" ") } : null;
      }).filter(Boolean),
      price_from: form.price_from ? Number(form.price_from) : null,
    };
    if (!payload.slug || !payload.meta_title || !payload.meta_description || !payload.h1) {
      toast.error("Slug, Meta title, Meta description and H1 are required"); return;
    }
    try {
      if (editing?._new) await api.post("/seo-pages", payload);
      else await api.patch(`/seo-pages/${editing.id}`, payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.h1}"?`)) return;
    try { await api.delete(`/seo-pages/${p.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const filtered = items.filter(p =>
    (langFilter === "all" || p.lang === langFilter) &&
    (kindFilter === "all" || p.kind === kindFilter) &&
    (!q || [p.slug, p.h1, p.meta_title, p.city, p.origin, p.destination].filter(Boolean).some(s => s.toLowerCase().includes(q.toLowerCase())))
  );

  return (
    <div data-testid="admin-seo-pages">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-black tracking-tight flex items-center gap-2">
            <Globe className="text-[#ea580c]" /> SEO Landing Pages
          </h1>
          <p className="text-sm text-slate-600 mt-1">Create route/vehicle/yatra pages. Sitemap.xml auto-updates. Supports English + मराठी.</p>
        </div>
        <div className="flex gap-2">
          <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="text-xs px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:border-[#ea580c] hover:text-[#ea580c] inline-flex items-center gap-1" data-testid="seo-sitemap-link"><ExternalLink className="w-3 h-3" /> View sitemap</a>
          <Button onClick={async () => {
            if (!window.confirm("Generate a Marathi twin for every English SEO page? Existing Marathi twins will be skipped.")) return;
            try {
              const r = await api.post("/seo-pages/generate-marathi-twins");
              toast.success(`Created ${r.data.created} Marathi twin(s). Skipped ${r.data.skipped}.`);
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
          }} variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 inline-flex items-center gap-1" data-testid="seo-gen-mr-twins">
            <Languages className="w-4 h-4" /> Generate मराठी twins
          </Button>
          <Button onClick={openNew} className="bg-[#ea580c] hover:bg-orange-700 inline-flex items-center gap-1" data-testid="seo-new"><Plus className="w-4 h-4" /> New page</Button>
        </div>
      </div>

      <div className="mt-4 grid md:grid-cols-4 gap-3">
        <div className="md:col-span-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search slug, H1, origin, destination…" value={q} onChange={e => setQ(e.target.value)} data-testid="seo-search" />
        </div>
        <Select value={kindFilter} onValueChange={setKindFilter}>
          <SelectTrigger data-testid="seo-kind-filter"><SelectValue placeholder="All kinds" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            {KINDS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={langFilter} onValueChange={setLangFilter}>
          <SelectTrigger data-testid="seo-lang-filter"><SelectValue placeholder="All languages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All languages</SelectItem>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="mr">मराठी (Marathi)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 grid gap-3">
        {filtered.length === 0 && <div className="p-8 rounded-2xl bg-white border border-dashed border-slate-300 text-center text-sm text-slate-500">No SEO pages yet. Click "New page" to create one.</div>}
        {filtered.map(p => (
          <div key={p.id} className="p-4 rounded-xl bg-white border border-slate-200 flex flex-wrap items-center justify-between gap-3" data-testid={`seo-row-${p.slug}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{p.active ? "Live" : "Draft"}</span>
                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{p.kind}</span>
                {p.lang === "mr" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 inline-flex items-center gap-1"><Languages className="w-3 h-3" />मराठी</span>}
                <span className="font-mono text-xs text-slate-500">/seo/{p.slug}</span>
              </div>
              <div className="font-display font-bold text-[#1e1b4b] mt-1 truncate">{p.h1}</div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">{p.meta_title}</div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <a href={`/seo/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-md border border-slate-200 hover:border-[#ea580c] hover:text-[#ea580c]" title="Preview" data-testid={`seo-preview-${p.slug}`}><ExternalLink className="w-4 h-4" /></a>
              <button onClick={() => openEdit(p)} className="p-2 rounded-md border border-slate-200 hover:border-[#1e1b4b] hover:text-[#1e1b4b]" data-testid={`seo-edit-${p.slug}`}><Edit className="w-4 h-4" /></button>
              <button onClick={() => del(p)} className="p-2 rounded-md border border-slate-200 hover:border-rose-500 hover:text-rose-500" data-testid={`seo-del-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?._new ? "New SEO Page" : "Edit SEO Page"}</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="grid md:grid-cols-3 gap-3">
              <div><Label>Slug *</Label><Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="pune-to-palus" data-testid="seo-form-slug" /></div>
              <div>
                <Label>Kind</Label>
                <Select value={form.kind} onValueChange={v => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="seo-form-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>{KINDS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Language</Label>
                <Select value={form.lang} onValueChange={v => setForm({ ...form, lang: v })}>
                  <SelectTrigger data-testid="seo-form-lang"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="mr">मराठी (Marathi)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div><Label>Meta title *</Label><Input value={form.meta_title} onChange={e => setForm({ ...form, meta_title: e.target.value })} placeholder="Pune to Palus Daily Service | Shruti Tours" data-testid="seo-form-title" /></div>
            <div><Label>Meta description * (150–160 chars)</Label><Textarea value={form.meta_description} onChange={e => setForm({ ...form, meta_description: e.target.value })} placeholder="Book daily Pune → Palus travel with AC/Non-AC Tempo Traveller and Ertiga. On-time departures, verified drivers. Instant WhatsApp quote." data-testid="seo-form-desc" /></div>
            <div><Label>H1 (page headline) *</Label><Input value={form.h1} onChange={e => setForm({ ...form, h1: e.target.value })} data-testid="seo-form-h1" /></div>
            <div><Label>Subtitle</Label><Input value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} /></div>

            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Hero image URL</Label><Input value={form.hero_image} onChange={e => setForm({ ...form, hero_image: e.target.value })} placeholder="https://…" /></div>
              <div><Label>OG image (social share)</Label><Input value={form.og_image} onChange={e => setForm({ ...form, og_image: e.target.value })} placeholder="https://…" /></div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div><Label>Origin</Label><Input value={form.origin} onChange={e => setForm({ ...form, origin: e.target.value })} placeholder="Pune" /></div>
              <div><Label>Destination</Label><Input value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })} placeholder="Palus" /></div>
              <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="Pune" /></div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div><Label>Vehicle type</Label><Input value={form.vehicle_type} onChange={e => setForm({ ...form, vehicle_type: e.target.value })} placeholder="Tempo Traveller" /></div>
              <div><Label>Price from (₹)</Label><Input type="number" value={form.price_from} onChange={e => setForm({ ...form, price_from: e.target.value })} /></div>
              <div><Label>Price note</Label><Input value={form.price_note} onChange={e => setForm({ ...form, price_note: e.target.value })} placeholder="per seat / per trip" /></div>
            </div>

            <div><Label>Highlights (one per line)</Label><Textarea value={pointsText} onChange={e => setPointsText(e.target.value)} placeholder={"Punctual daily departures\nAC & Non-AC options\nExperienced drivers"} rows={4} /></div>
            <div><Label>Inclusions (one per line)</Label><Textarea value={inclText} onChange={e => setInclText(e.target.value)} placeholder={"Fuel + toll\nDriver allowance\nPickup at your location"} rows={4} /></div>

            <div><Label>FAQs (Question line, Answer line, `---` between)</Label><Textarea value={faqsText} onChange={e => setFaqsText(e.target.value)} rows={7} placeholder={"What time does the daily service run?\n6:00 AM from Palus, 4:00 PM from Pune.\n---\nDo you provide AC vehicles?\nYes, both AC and non-AC options available."} /></div>

            <div><Label>Body HTML / rich content</Label><Textarea value={form.body_html} onChange={e => setForm({ ...form, body_html: e.target.value })} rows={6} placeholder="<p>Full article content here…</p>" /></div>

            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Related slugs (comma-separated)</Label><Input value={relText} onChange={e => setRelText(e.target.value)} placeholder="palus-to-pune, pune-to-sangli, tempo-traveller-rental" /></div>
              <div><Label>Schema service type</Label><Input value={form.schema_service_type} onChange={e => setForm({ ...form, schema_service_type: e.target.value })} placeholder="Vehicle rental" /></div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Primary CTA label</Label><Input value={form.primary_cta_label} onChange={e => setForm({ ...form, primary_cta_label: e.target.value })} /></div>
              <div><Label>Primary CTA link</Label><Input value={form.primary_cta_url} onChange={e => setForm({ ...form, primary_cta_url: e.target.value })} /></div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <div className="flex items-center gap-2">
                <input id="active" type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} data-testid="seo-form-active" />
                <Label htmlFor="active" className="!m-0">Active (live)</Label>
              </div>
              <div><Label>Sitemap priority (0-1)</Label><Input type="number" step="0.1" min="0" max="1" value={form.priority} onChange={e => setForm({ ...form, priority: Number(e.target.value) })} /></div>
              <div>
                <Label>Change frequency</Label>
                <Select value={form.change_freq} onValueChange={v => setForm({ ...form, change_freq: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["daily", "weekly", "monthly", "yearly"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-[#ea580c] hover:bg-orange-700" data-testid="seo-form-save">Save page</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
